import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Grid2,
  Paper,
  Skeleton,
  Tooltip,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { modernPageShellSx } from "../../../../../styles/modernListingPageSx";
import {
  AlertActivityAction,
  AlertRecipientReason,
  canResolveAlert,
} from "../../../../../models/Alerts/Alert";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import { trackAlertsAction } from "../../../../../analytics/alertsTracking";
import ResolveAlertDialog from "../../components/alerts/ResolveAlertDialog";
import { AlertPill, SeverityPill } from "../../components/alerts/AlertPills";
import {
  SectionIcon,
  formatAlertDateTime,
  formatAlertRelative,
  formatPayloadKey,
  getAlertColors,
  getResolvedTone,
  getSeverityTone,
} from "../../components/alerts/alertUi";

const REASON_LABELS: Record<AlertRecipientReason, string> = {
  DIRECT: "Direct",
  ROLE: "Role",
  SUBSCRIPTION: "Following",
};

const ACTIVITY_LABELS: Record<AlertActivityAction, string> = {
  CREATED: "Alert raised",
  READ: "Read",
  RESOLVED: "Resolved",
  ARCHIVED: "Archived",
  UNARCHIVED: "Moved back to inbox",
};

const formatPayloadValue = (value: unknown): string => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const AlertDetailPage: React.FC = () => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const c = theme.palette.customColors;
  const navigate = useNavigate();
  const { id } = useParams();
  const alertId = Number(id);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  useEffect(() => {
    alertsStore.loadMeta();
    if (Number.isFinite(alertId) && alertId > 0) {
      alertsStore.loadDetail(alertId);
    }
    return () => alertsStore.clearDetail();
  }, [alertId]);

  const alert = alertsStore.detail;
  const isInvalidId = !Number.isFinite(alertId) || alertId <= 0;

  const cardSx = {
    p: { xs: 2, md: 2.5 },
    borderRadius: "16px",
    border: `1px solid ${colors.border}`,
    bgcolor: colors.surface,
    boxShadow: `0 8px 24px ${c.shadowVeryLight}`,
  };

  const backButton = (
    <Box
      component="button"
      type="button"
      onClick={() => {
        trackAlertsAction("back", "dtl", alert ? { alert } : {});
        navigate("/alerts");
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mb: 1.5,
        p: 0,
        border: 0,
        bgcolor: "transparent",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "0.8rem",
        fontWeight: 600,
        color: colors.subtitle,
        "&:hover": { color: colors.accent },
      }}
    >
      <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
      All alerts
    </Box>
  );

  if (isInvalidId || (alertsStore.detailError && !alert)) {
    return (
      <Grid2 container sx={{ ...modernPageShellSx(theme), minHeight: "auto" }}>
        <Grid2 size={12}>
          {backButton}
          <Paper elevation={0} sx={{ ...cardSx, textAlign: "center", py: 6 }}>
            <CustomTypography variant="heading6" color={colors.title}>
              {alertsStore.detailNotFound || isInvalidId
                ? "Alert not found"
                : "Couldn't load this alert"}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={c.greyTextColor}
              sx={{ mt: 0.5, mb: 2 }}
            >
              {isInvalidId
                ? "This link doesn't point to a valid alert."
                : alertsStore.detailError}
            </CustomTypography>
            {!alertsStore.detailNotFound && !isInvalidId && (
              <AppButton
                label="Try again"
                variant="transparentPrimary"
                onClick={() => {
                  trackAlertsAction("retry", "dtl");
                  alertsStore.loadDetail(alertId);
                }}
              />
            )}
          </Paper>
        </Grid2>
      </Grid2>
    );
  }

  if (!alert) {
    return (
      <Grid2 container sx={{ ...modernPageShellSx(theme), minHeight: "auto" }}>
        <Grid2 size={12}>
          {backButton}
          <Paper elevation={0} sx={cardSx}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton
                variant="rounded"
                width={52}
                height={52}
                sx={{ borderRadius: "14px" }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton width={180} height={22} />
                <Skeleton width="60%" height={34} />
                <Skeleton width="40%" height={18} />
              </Box>
            </Box>
          </Paper>
          <Grid2 container spacing={2} sx={{ mt: 2 }}>
            <Grid2 size={{ xs: 12, md: 8 }}>
              <Skeleton
                variant="rounded"
                height={220}
                sx={{ borderRadius: "16px" }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Skeleton
                variant="rounded"
                height={220}
                sx={{ borderRadius: "16px" }}
              />
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    );
  }

  const tone = getSeverityTone(theme, alert.severity);
  const resolvedTone = getResolvedTone(theme);
  const isResolved = alert.status === "RESOLVED";
  const isArchived = alert.me.archived_at != null;
  const route = alertsStore.webRoute(alert);
  const refType = alertsStore.refType(alert.ref.type);
  const payloadEntries = Object.entries(alert.payload ?? {});
  const readCount = alert.recipients.filter((r) => r.read_at != null).length;

  const handleOpenRef = (placement: "header" | "card") => {
    if (!route) return;
    trackAlertsAction("open_ref", "dtl", { alert, placement });
    navigate(route);
  };

  const handleArchiveToggle = async () => {
    trackAlertsAction(isArchived ? "unarchive" : "archive", "dtl", { alert });
    setIsArchiving(true);
    setActionError("");
    setActionSuccess("");
    try {
      if (isArchived) {
        await alertsStore.unarchive([alert.id]);
        setActionSuccess("Moved back to your inbox.");
      } else {
        await alertsStore.archive([alert.id]);
        setActionSuccess("Archived. It's hidden from your inbox.");
      }
    } catch (error: any) {
      setActionError(error?.message || "Failed to update alert.");
    } finally {
      setIsArchiving(false);
    }
  };

  const statusPill = isResolved ? (
    <AlertPill label="Resolved" tone={resolvedTone} dot />
  ) : alert.status === "OPEN" ? (
    <AlertPill label="Open" tone={{ ...tone, bg: "transparent" }} dot />
  ) : (
    <AlertPill label="Informational" />
  );

  return (
    <Grid2 container sx={{ ...modernPageShellSx(theme), minHeight: "auto" }}>
      <Grid2 size={12}>
        {backButton}

        {/* Hero */}
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: "0 0 auto 0",
              height: 4,
              bgcolor: isResolved ? resolvedTone.fg : tone.fg,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: isResolved ? resolvedTone.fg : tone.fg,
                bgcolor: isResolved ? resolvedTone.bg : tone.bg,
                border: `1px solid ${isResolved ? resolvedTone.border : tone.border}`,
              }}
            >
              {isResolved ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 26 }} />
              ) : (
                <SectionIcon
                  icon={alertsStore.sectionIcon(alert.section)}
                  section={alert.section}
                  sx={{ fontSize: 26 }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 0.75 }}
              >
                <SeverityPill severity={alert.severity} />
                {statusPill}
                <AlertPill label={alertsStore.sectionLabel(alert.section)} />
                {alert.occurrence_count > 1 && (
                  <AlertPill
                    label={`×${alert.occurrence_count} occurrences`}
                    tone={tone}
                  />
                )}
                {isArchived && <AlertPill label="Archived" />}
              </Box>
              <CustomTypography
                variant="heading3"
                color={colors.title}
                sx={{ letterSpacing: "-0.01em", wordBreak: "break-word" }}
              >
                {alert.title}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={colors.subtitle}
                sx={{ mt: 0.75 }}
              >
                Raised {formatAlertDateTime(alert.created_at)}
                {alert.last_occurred_at !== alert.created_at &&
                  ` · last occurred ${formatAlertRelative(alert.last_occurred_at)} ago`}
              </CustomTypography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <AppButton
                label={isArchived ? "Unarchive" : "Archive"}
                variant="grayscale"
                startIcon={
                  isArchiving ? (
                    <CircularProgress size={14} />
                  ) : isArchived ? (
                    <UnarchiveOutlinedIcon />
                  ) : (
                    <ArchiveOutlinedIcon />
                  )
                }
                onClick={handleArchiveToggle}
                disabled={isArchiving}
              />
              {route && (
                <AppButton
                  label={`Open ${alert.ref.display}`}
                  variant="transparentPrimary"
                  endIcon={<OpenInNewRoundedIcon />}
                  onClick={() => handleOpenRef("header")}
                />
              )}
              {canResolveAlert(alert) && (
                <AppButton
                  label="Resolve"
                  variant="primary"
                  startIcon={<TaskAltRoundedIcon />}
                  onClick={() => {
                    trackAlertsAction("resolve_open", "dtl", { alert });
                    setIsResolveOpen(true);
                  }}
                />
              )}
            </Box>
          </Box>
          <InlineFormError message={actionError} sx={{ mt: 1.5, mb: 0 }} />
          {actionSuccess && (
            <CustomTypography
              variant="heading8"
              color={c.successGreen}
              sx={{ mt: 1.5 }}
            >
              {actionSuccess}
            </CustomTypography>
          )}
        </Paper>

        <Grid2 container spacing={2} sx={{ mt: 2 }}>
          {/* Main column */}
          <Grid2
            size={{ xs: 12, md: 8 }}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {isResolved && alert.resolution && (
              <Paper
                elevation={0}
                sx={{
                  ...cardSx,
                  bgcolor: resolvedTone.bg,
                  border: `1px solid ${resolvedTone.border}`,
                  boxShadow: "none",
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <CheckCircleRoundedIcon
                    sx={{ color: resolvedTone.fg, mt: 0.25 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <CustomTypography variant="heading7" color={colors.title}>
                      Resolved by {alert.resolution.by?.name ?? "an admin"}
                    </CustomTypography>
                    <CustomTypography variant="caption" color={colors.subtitle}>
                      {formatAlertDateTime(alert.resolution.at)}
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      color={colors.text}
                      sx={{
                        mt: 1,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      “{alert.resolution.note}”
                    </CustomTypography>
                  </Box>
                </Box>
              </Paper>
            )}

            <Paper elevation={0} sx={cardSx}>
              <CustomTypography variant="heading7" color={colors.title}>
                Details
              </CustomTypography>
              <CustomTypography
                variant="body1"
                color={alert.body ? colors.text : colors.muted}
                sx={{ mt: 1, lineHeight: 1.6, whiteSpace: "pre-wrap" }}
              >
                {alert.body || "No additional description."}
              </CustomTypography>

              {payloadEntries.length > 0 && (
                <Grid2
                  container
                  spacing={1.5}
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${colors.hairline}`,
                  }}
                >
                  {payloadEntries.map(([key, value]) => (
                    <Grid2 key={key} size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          height: "100%",
                          borderRadius: "10px",
                          bgcolor: colors.surfaceMuted,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <CustomTypography
                          variant="heading9"
                          color={colors.subtitle}
                        >
                          {formatPayloadKey(key)}
                        </CustomTypography>
                        <CustomTypography
                          variant="body1"
                          color={colors.title}
                          fontWeight={600}
                          sx={{ mt: 0.5, wordBreak: "break-word" }}
                        >
                          {formatPayloadValue(value)}
                        </CustomTypography>
                      </Box>
                    </Grid2>
                  ))}
                </Grid2>
              )}
            </Paper>

            {/* Timeline */}
            <Paper elevation={0} sx={cardSx}>
              <CustomTypography variant="heading7" color={colors.title}>
                Activity
              </CustomTypography>
              <Box sx={{ mt: 2 }}>
                {alert.activity.length === 0 && (
                  <CustomTypography variant="body2" color={colors.muted}>
                    No activity yet.
                  </CustomTypography>
                )}
                {alert.activity.map((entry, index) => {
                  const isLast = index === alert.activity.length - 1;
                  const dotColor =
                    entry.action === "RESOLVED"
                      ? resolvedTone.fg
                      : entry.action === "CREATED"
                        ? tone.fg
                        : colors.muted;
                  return (
                    <Box
                      key={`${entry.action}-${entry.at}-${index}`}
                      sx={{ display: "flex", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: 14,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            mt: 0.6,
                            borderRadius: "50%",
                            bgcolor: dotColor,
                            boxShadow: `0 0 0 3px ${colors.surface}, 0 0 0 4px ${colors.border}`,
                          }}
                        />
                        {!isLast && (
                          <Box
                            sx={{
                              flex: 1,
                              width: 2,
                              my: 0.5,
                              bgcolor: colors.hairline,
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ pb: isLast ? 0 : 2, minWidth: 0 }}>
                        <CustomTypography
                          variant="body1"
                          color={colors.title}
                          fontWeight={600}
                        >
                          {ACTIVITY_LABELS[entry.action] ?? entry.action}
                          <Box
                            component="span"
                            sx={{ color: colors.subtitle, fontWeight: 500 }}
                          >
                            {entry.by ? ` by ${entry.by.name}` : " by system"}
                          </Box>
                        </CustomTypography>
                        <CustomTypography
                          variant="caption"
                          color={colors.muted}
                        >
                          {formatAlertDateTime(entry.at)}
                        </CustomTypography>
                        {entry.note && (
                          <Box
                            sx={{
                              mt: 0.75,
                              px: 1.25,
                              py: 1,
                              borderRadius: "8px",
                              bgcolor: colors.surfaceMuted,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color={colors.text}
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {entry.note}
                            </CustomTypography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid2>

          {/* Side column */}
          <Grid2
            size={{ xs: 12, md: 4 }}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Paper elevation={0} sx={cardSx}>
              <CustomTypography variant="heading7" color={colors.title}>
                Reference
              </CustomTypography>
              <Box
                component={route ? "button" : "div"}
                type={route ? "button" : undefined}
                onClick={route ? () => handleOpenRef("card") : undefined}
                sx={{
                  mt: 1.5,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  p: 1.5,
                  textAlign: "left",
                  fontFamily: "inherit",
                  borderRadius: "12px",
                  bgcolor: colors.surfaceMuted,
                  border: `1px solid ${colors.border}`,
                  cursor: route ? "pointer" : "default",
                  transition: "border-color 0.15s ease",
                  "&:hover": route ? { borderColor: colors.accent } : {},
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTypography variant="heading9" color={colors.subtitle}>
                    {refType?.label ?? alert.ref.type}
                  </CustomTypography>
                  <CustomTypography
                    variant="heading6"
                    color={colors.title}
                    sx={{
                      mt: 0.4,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {alert.ref.display}
                  </CustomTypography>
                </Box>
                {route && (
                  <OpenInNewRoundedIcon
                    sx={{ fontSize: 18, color: colors.accent }}
                  />
                )}
              </Box>
              {alert.parent_ref && (
                <CustomTypography
                  variant="caption"
                  color={colors.subtitle}
                  sx={{ mt: 1 }}
                >
                  Part of{" "}
                  {alertsStore.refType(alert.parent_ref.type)?.label ??
                    alert.parent_ref.type}{" "}
                  #{alert.parent_ref.id}
                </CustomTypography>
              )}
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                <CustomTypography variant="caption" color={colors.subtitle}>
                  You received this via{" "}
                  <b>
                    {REASON_LABELS[alert.me.reason]?.toLowerCase() ??
                      alert.me.reason}
                  </b>{" "}
                  routing.
                </CustomTypography>
                <CustomTypography variant="caption" color={colors.muted}>
                  Type: {alert.type_code}
                </CustomTypography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={cardSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <CustomTypography variant="heading7" color={colors.title}>
                  Recipients
                </CustomTypography>
                <CustomTypography variant="caption" color={colors.subtitle}>
                  {readCount}/{alert.recipients.length} read
                </CustomTypography>
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {alert.recipients.map((recipient) => (
                  <Box
                    key={recipient.admin_id}
                    sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: colors.unreadBg,
                        color: colors.accent,
                      }}
                    >
                      {recipient.name?.charAt(0)?.toUpperCase() || "?"}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <CustomTypography
                        variant="body1"
                        color={colors.title}
                        fontWeight={600}
                        sx={{
                          fontSize: "0.82rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {recipient.name}
                      </CustomTypography>
                      <CustomTypography variant="caption" color={colors.muted}>
                        {REASON_LABELS[recipient.reason] ?? recipient.reason}
                      </CustomTypography>
                    </Box>
                    {recipient.read_at ? (
                      <Tooltip
                        title={`Read ${formatAlertDateTime(recipient.read_at)}`}
                      >
                        <DoneAllRoundedIcon
                          sx={{ fontSize: 18, color: c.successGreen }}
                        />
                      </Tooltip>
                    ) : (
                      <AlertPill label="Unread" />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
      </Grid2>

      <ResolveAlertDialog
        alert={alert}
        open={isResolveOpen}
        onClose={() => setIsResolveOpen(false)}
        onResolved={() => setActionSuccess("Alert resolved for everyone.")}
      />
    </Grid2>
  );
};

export default observer(AlertDetailPage);
